import React from 'react';
import { FileText, Eye } from 'lucide-react';
import type { LegalFile, Client } from '../../types';
import ListItemCard from '../../components/ui/ListItemCard';

interface LegalFilesListPanelProps {
  legalFiles: LegalFile[];
  filteredFiles: LegalFile[];
  selectedClient: Client | null;
  selectedFile: LegalFile | null;
  loading: boolean;
  searchQuery: string;
  filterStatus: string;
  language: 'en' | 'ar';
  strings: any;
  getTypeLabel: (typeID: string) => string;
  getStatusBadge: (statusID: string) => React.ReactNode;
  onViewDetails: (file: LegalFile) => void;
}

const LegalFilesListPanel: React.FC<LegalFilesListPanelProps> = ({
  legalFiles,
  filteredFiles,
  selectedClient,
  selectedFile,
  loading,
  searchQuery,
  filterStatus,
  language,
  strings,
  getTypeLabel,
  getStatusBadge,
  onViewDetails
}) => {
  return (
    <div className="lg:col-span-2">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                {strings.legalFiles.legalFilesList}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {selectedClient 
                  ? language === 'ar' 
                    ? `تم العثور على ${filteredFiles.length} ملف${filteredFiles.length !== 1 ? 'ات' : ''}`
                    : `${filteredFiles.length} file${filteredFiles.length !== 1 ? 's' : ''} found`
                  : language === 'ar' 
                    ? 'اختر الموكل أولاً'
                    : 'Select a client first'
                }
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-4 max-h-96 overflow-y-auto">
          {!selectedClient ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
                {strings.legalFiles.selectClient}
              </h3>
              <p className="text-slate-500 dark:text-slate-500">
                {language === 'ar' 
                  ? 'اختر موكلاً لعرض ملفاته القانونية'
                  : 'Choose a client to view their legal files'
                }
              </p>
            </div>
          ) : loading ? (
            <div className="text-center py-16">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500 dark:text-slate-400">
                {language === 'ar' ? 'جاري تحميل الملفات القانونية...' : 'Loading legal files...'}
              </p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
                {strings.legalFiles.noFiles}
              </h3>
              <p className="text-slate-500 dark:text-slate-500">
                {searchQuery || filterStatus !== 'All Status' 
                  ? (language === 'ar' 
                      ? 'حاول تعديل معايير البحث أو التصفية'
                      : 'Try adjusting your search or filter criteria'
                    )
                  : (language === 'ar' 
                      ? 'لم يتم إنشاء ملفات قانونية لهذا الموكل بعد'
                  : 'No legal files have been created for this client yet'
                    )
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFiles.map((file) => {
                const isSelected = selectedFile?.id === file.id;
                
                return (
                  <div
                    key={file.id}
                    className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-primary shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1" onClick={() => onViewDetails(file)}>
                        <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                          📄
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 dark:text-white text-sm truncate">
                            {file.title}
                          </h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {file.caseNumber} • {getTypeLabel(file.typeID || '')}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(file.statusID || '')}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails(file);
                        }}
                        className="ml-3 px-3 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-1"
                      >
                        <Eye className="w-3 h-3" />
                        <span>{language === 'ar' ? 'عرض التفاصيل' : 'View Details'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegalFilesListPanel;
